/**
 * CartStore — single source of truth for /cart.js data.
 *
 * Option 2: Deduplicating cache
 *   CartStore.get()          → Promise<cart>  returns cache; fetches once if stale
 *   CartStore.refresh()      → Promise<cart>  always fetches fresh; updates cache
 *   CartStore.setCache(cart) → void           populate cache from a known-fresh object (no fetch)
 *   CartStore.invalidate()   → void           mark cache stale so next get() fetches
 *
 * Option 3: Event-driven cache population
 *   Listens to cart write events (cart:item-remove, cart:item-quantity-update,
 *   cart:sections:updated). When the event carries e.detail.cart the cache is
 *   populated directly — zero extra network round-trip. When it does not, the
 *   cache is invalidated so the next get() fetches fresh data.
 *
 * Load order: must be loaded (and deferred) before scripts.js, custom.js and
 * store-selector.js — add it to snippets/head-scripts.liquid first.
 */
window.CartStore = (function () {
  'use strict';

  var _cache    = null;  // last known cart object
  var _inFlight = null;  // in-flight Promise — deduplicates concurrent callers

  // Internal: always makes a real network request; deduplicates concurrent calls.
  function _doFetch() {
    if (_inFlight) return _inFlight;
    var _fetch = (window.CartQueue ? window.CartQueue.fetch : fetch);
    _inFlight = _fetch('/cart.js')
      .then(function (r) {
        // A non-OK response (typically the 429 edge bot-protection checkpoint)
        // returns an HTML challenge page — never hand that to JSON.parse, or it
        // throws "Unexpected token '<'". Surface a typed error instead.
        if (!r.ok) {
          var httpErr = new Error('CartStore /cart.js HTTP ' + r.status);
          httpErr.status = r.status;
          throw httpErr;
        }
        return r.json();
      })
      .then(function (cart) {
        _cache    = cart;
        _inFlight = null;
        document.dispatchEvent(
          new CustomEvent('cart:state:updated', { detail: { cart: cart } })
        );
        return cart;
      })
      .catch(function (err) {
        _inFlight = null;
        // Degrade gracefully: if we already have a last-known cart, serve it so
        // callers (delivery price / inventory UI) don't crash on the checkpoint.
        // Only propagate when there is nothing cached to fall back to.
        if (_cache) return _cache;
        throw err;
      });
    return _inFlight;
  }

  var store = {
    /**
     * Return the cached cart if available, otherwise fetch once.
     * All concurrent callers share a single in-flight request.
     */
    get: function () {
      if (_cache) return Promise.resolve(_cache);
      return _doFetch();
    },

    /**
     * Force a fresh fetch regardless of the current cache state.
     * Use this after a cart mutation when the response did NOT return
     * the full updated cart object.
     */
    refresh: function () {
      _cache    = null;
      _inFlight = null;
      return _doFetch();
    },

    /**
     * Populate the cache from an already-known-fresh cart object.
     * Avoids a network round-trip when a write operation (e.g. /cart/change.js)
     * already returned the full updated cart.
     */
    setCache: function (cart) {
      if (!cart) return;
      _cache    = cart;
      _inFlight = null;
    },

    /**
     * Synchronously return the last-known cart object without triggering a
     * fetch. Returns null when nothing is cached yet. Use for cheap, non-async
     * reads (e.g. a quantity-dedupe guard) where a stale-or-missing value is
     * acceptable.
     */
    peek: function () {
      return _cache;
    },

    /**
     * Mark the cache as stale. The next call to get() will fetch fresh data.
     */
    invalidate: function () {
      _cache    = null;
      _inFlight = null;
    }
  };

  // ── Option 3: event-driven cache population ──────────────────────────────────
  // Cart write events already carry the fresh cart in e.detail.cart — feed it
  // straight into the cache so subsequent get() calls need no network round-trip.
  // When no cart data comes with the event, invalidate so the next get() refetches.
  function _onCartEvent(e) {
    var freshCart = e && e.detail && e.detail.cart;
    if (freshCart) {
      store.setCache(freshCart);
    } else {
      store.invalidate();
    }
  }

  [
    'cart:item-remove',
    'cart:item-quantity-update',
    'cart:sections:updated'
  ].forEach(function (eventName) {
    document.addEventListener(eventName, _onCartEvent);
  });

  // ── Initial page-load fetch ───────────────────────────────────────────────────
  // Deferred scripts run after HTML parsing (readyState === 'interactive'),
  // before DOMContentLoaded — so we can call refresh() immediately here.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { store.refresh(); });
  } else {
    store.refresh();
  }

  return store;
})();
